/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminActivityActions,
  adminActivityAnalytics,
  adminBlockUser,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminUnblockUser,
  adminUpdateUser,
  changePassword,
  composerAddToLibrary,
  composerAsk,
  composerCompose,
  composerConfirmDeck,
  composerConfirmEscalation,
  composerConfirmFormats,
  composerConfirmPages,
  composerConfirmRevision,
  composerDeleteAsset,
  composerFetchAsset,
  composerGetArtifactPreview,
  composerGetDocument,
  composerIntake,
  composerListAssets,
  composerListEscalations,
  composerListLibraryQueue,
  composerListRules,
  composerListTurns,
  composerLocalizePdf,
  composerPreviewTurnRemoval,
  composerRefine,
  composerRemoveTurn,
  composerRenameTurn,
  composerTranslationStatus,
  composerUploadAssets,
  getActivity,
  getHealth,
  getMe,
  getMeDetail,
  login,
  logout,
  type RequestSpec,
  signup,
  type SpecCorrection,
  updateProfile,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data;
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

/**
 * Take someone's access away without deleting them.
 *
 * Anyone on an allowed domain can self-register, so the lever an admin needs
 * for an unwanted visitor is usually "stop letting them in", not "erase the
 * record of them having been here".
 */
export function useBlockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data } = await adminBlockUser({ path: { user_id: vars.userId } });
      logger.debug("[admin]", "blocked user", vars.userId);
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useUnblockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data } = await adminUnblockUser({ path: { user_id: vars.userId } });
      logger.debug("[admin]", "unblocked user", vars.userId);
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Composer ────────────────────────────────────────────────────────────

// Anything that produces an artifact can draft new tier-3 briefs, so they all
// stale the same escalation list.
const COMPOSER_ESCALATIONS_KEY = ["composer", "escalations"];
const COMPOSER_TURNS_KEY = ["composer", "turns"];
const COMPOSER_LIBRARY_KEY = ["composer", "library"];
const COMPOSER_ASSETS_KEY = ["composer", "assets"];

/**
 * Ask the composer for an asset. It decides which of the four outcomes the
 * request lands in — hand over an approved asset, build a deck, propose pages
 * to keep, or draft a brief for Strategic Comms — and returns a turn saying so.
 */
export function useComposerAsk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      requestText: string;
      corrections?: SpecCorrection;
      previousTurnId?: string;
    }) => {
      const { data } = await composerAsk({
        body: {
          request_text: vars.requestText,
          corrections: vars.corrections ?? null,
          previous_turn_id: vars.previousTurnId ?? null,
        },
      });
      logger.debug("[composer]", "asked ->", data?.outcome, "tier", data?.tier);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: COMPOSER_TURNS_KEY });
    },
  });
}

/**
 * Offer a generated file back to the asset library.
 *
 * Queues it for Strategic Comms rather than making it searchable — the market
 * that generated it can decide it is worth keeping, but not that it counts as
 * approved brand material.
 */
export function useComposerAddToLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { artifactId: string }) => {
      const { data } = await composerAddToLibrary({ path: { artifact_id: vars.artifactId } });
      logger.debug("[composer]", "queued for the library", vars.artifactId, data?.entry.status);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_LIBRARY_KEY });
    },
  });
}

/** Everything markets have offered back to the library, newest first. */
export function useComposerLibraryQueue() {
  return useQuery({
    queryKey: COMPOSER_LIBRARY_KEY,
    queryFn: async () => {
      const { data } = await composerListLibraryQueue();
      return data ?? [];
    },
  });
}

/** Complete a `propose_pdf_pages` turn with the pages the requestor chose. */
export function useComposerConfirmPages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { turnId: string; pages: number[] }) => {
      const { data } = await composerConfirmPages({
        path: { turn_id: vars.turnId },
        body: { pages: vars.pages },
      });
      logger.debug("[composer]", "localized pages", data?.pages_kept);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
      // The confirmed pages are saved onto the turn, so the history is now
      // stale — and the rail lists a conversation's files from it. Without
      // this the localized extract was missing from the version list until
      // something else happened to refetch.
      void queryClient.invalidateQueries({ queryKey: COMPOSER_TURNS_KEY });
    },
  });
}

/**
 * Rendered page images for a generated artifact.
 *
 * The first render shells out to LibreOffice and takes several seconds; every
 * one after that is served from cache. Kept fresh forever because a given
 * artifact id never changes its file.
 */
export function useComposerPreview(artifactId: string | undefined) {
  return useQuery({
    queryKey: ["composer", "preview", artifactId],
    queryFn: async () => {
      if (!artifactId) {
        return undefined;
      }
      const { data } = await composerGetArtifactPreview({ path: { artifact_id: artifactId } });
      return data;
    },
    enabled: Boolean(artifactId),
    staleTime: Infinity,
  });
}

/** The most recent requests, so a reload does not lose the thread. */
export function useComposerTurns(limit = 10) {
  return useQuery({
    queryKey: [...COMPOSER_TURNS_KEY, limit],
    queryFn: async () => {
      const { data } = await composerListTurns({ query: { limit } });
      return data ?? [];
    },
  });
}

/** Build the deck a requestor approved — the expensive half of a deck turn. */
export function useComposerConfirmDeck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { turnId: string }) => {
      const { data } = await composerConfirmDeck({ path: { turn_id: vars.turnId } });
      logger.debug("[composer]", "built deck", data?.filename);
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: COMPOSER_TURNS_KEY });
      // Warm the preview now rather than when someone clicks the file. The
      // first render shells out to LibreOffice and takes several seconds; doing
      // it here hides that behind the wait they already accepted for the build.
      if (data?.artifact_id) {
        void queryClient.prefetchQuery({
          queryKey: ["composer", "preview", data.artifact_id],
          queryFn: async () => {
            const { data: preview } = await composerGetArtifactPreview({
              path: { artifact_id: data.artifact_id },
            });
            return preview;
          },
          staleTime: Infinity,
        });
      }
    },
  });
}

/**
 * Rebuild a deck with only the slide edits the requestor approved.
 *
 * The same shape of moment as the deck confirm, and deliberately so: the edits
 * are already read back on the turn, and this is the step that reopens the
 * slides that were stored and writes the file again. What it is *not* is a
 * recompose — every slide nobody named comes back byte for byte, which is the
 * whole promise the readback made.
 */
export function useComposerConfirmRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { turnId: string }) => {
      const { data } = await composerConfirmRevision({ path: { turn_id: vars.turnId } });
      logger.debug("[composer]", "revised deck", data?.filename);
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: COMPOSER_TURNS_KEY });
      // Same reasoning as the deck build: render the preview while the wait is
      // still expected, not when someone clicks the file.
      if (data?.artifact_id) {
        void queryClient.prefetchQuery({
          queryKey: ["composer", "preview", data.artifact_id],
          queryFn: async () => {
            const { data: preview } = await composerGetArtifactPreview({
              path: { artifact_id: data.artifact_id },
            });
            return preview;
          },
          staleTime: Infinity,
        });
      }
    },
  });
}

/**
 * Render a chosen creative route in the formats the requestor ticked.
 *
 * The expensive half of a social-ad turn, in the same shape as the deck
 * confirm: the plan — the routes on offer, each with its own photo and copy,
 * and the formats — is already on the turn, and this is the step that writes
 * files. Naming the route is what makes the choice between ideas real.
 */
export function useComposerConfirmFormats() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      turnId: string;
      formatKeys: string[];
      imageAssetId?: string;
      routeKey?: string;
    }) => {
      const { data } = await composerConfirmFormats({
        path: { turn_id: vars.turnId },
        body: {
          format_keys: vars.formatKeys,
          // Sent only when the requestor swapped the chosen route's photo for
          // one of the alternatives — omitted, the route keeps its own.
          image_asset_id: vars.imageAssetId,
          // Which of the creative routes to render. Absent only for turns
          // remembered from before routes existed, where the plan's flat
          // fields are the only proposal there is.
          route_key: vars.routeKey,
        },
      });
      logger.debug(
        "[composer]",
        "rendered social ads",
        vars.formatKeys,
        "route:",
        vars.routeKey ?? "(none on this plan)",
        "photo:",
        vars.imageAssetId ?? "(the route's own)",
        "->",
        data?.artifact_id,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
      // The renders are saved onto the turn, so the remembered history — which
      // is what the rail lists a conversation's versions from — is now stale.
      void queryClient.invalidateQueries({ queryKey: COMPOSER_TURNS_KEY });
    },
  });
}

/** Send — or decline to send — a drafted brief to Strategic Comms. */
export function useComposerConfirmEscalation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { turnId: string; confirmed: boolean; note?: string }) => {
      const { data } = await composerConfirmEscalation({
        path: { turn_id: vars.turnId },
        body: { confirmed: vars.confirmed, note: vars.note ?? "" },
      });
      logger.debug("[composer]", "escalation sent:", data?.sent);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
    },
  });
}

export function useComposerAssets() {
  return useQuery({
    queryKey: COMPOSER_ASSETS_KEY,
    queryFn: async () => {
      const { data } = await composerListAssets();
      logger.debug(
        "[composer]",
        "ingested assets",
        data?.assets.length,
        "template",
        data?.template?.filename,
      );
      return data;
    },
  });
}

export function useComposerUploadAssets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { files: File[] }) => {
      const { data } = await composerUploadAssets({ body: { files: vars.files } });
      logger.debug("[composer]", "uploaded", vars.files.length, "files");
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ASSETS_KEY });
    },
  });
}

/** What removing a request would take with it — asked before it is removed. */
export function useComposerTurnRemovalPreview(turnId: string | undefined) {
  return useQuery({
    queryKey: [...COMPOSER_TURNS_KEY, turnId, "removal"],
    enabled: Boolean(turnId),
    queryFn: async () => {
      const { data } = await composerPreviewTurnRemoval({ path: { turn_id: turnId as string } });
      return data;
    },
  });
}

/** Remove one request from the history, and the files only it holds. */
export function useComposerRemoveTurn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { turnId: string }) => {
      const { data } = await composerRemoveTurn({ path: { turn_id: vars.turnId } });
      logger.debug("[composer]", "removed request", vars.turnId, data?.files_lost);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_TURNS_KEY });
      void queryClient.invalidateQueries({ queryKey: COMPOSER_LIBRARY_KEY });
    },
  });
}

/**
 * Name one conversation in the history, or clear the name again.
 *
 * An empty name is a real request rather than a mistake: it is how someone
 * undoes a rename, and the rail falls back to the title derived from the
 * request itself.
 */
export function useComposerRenameTurn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { turnId: string; name: string }) => {
      const { data } = await composerRenameTurn({
        path: { turn_id: vars.turnId },
        body: { name: vars.name },
      });
      logger.debug("[composer]", "renamed request", vars.turnId, "->", data?.name);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_TURNS_KEY });
    },
  });
}

/** Pull a source file into the library from a public link. */
export function useComposerFetchAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { url: string }) => {
      const { data } = await composerFetchAsset({ body: { url: vars.url } });
      logger.debug("[composer]", "fetched", vars.url);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ASSETS_KEY });
    },
  });
}

export function useComposerDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { assetId: string }) => {
      const { data } = await composerDeleteAsset({ path: { asset_id: vars.assetId } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ASSETS_KEY });
    },
  });
}

export function useComposerIntake() {
  return useMutation({
    mutationFn: async (vars: { requestText: string }) => {
      const { data } = await composerIntake({ body: { request_text: vars.requestText } });
      logger.debug("[composer]", "intake →", data?.work_mode, data?.spec);
      return data;
    },
  });
}

export function useComposerCompose() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestText: string; spec: RequestSpec; assetIds?: string[] }) => {
      const { data } = await composerCompose({
        body: {
          request_text: vars.requestText,
          spec: vars.spec,
          asset_ids: vars.assetIds ?? [],
        },
      });
      logger.debug("[composer]", "composed", data?.artifact_id, data?.tier_summary);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
    },
  });
}

export function useComposerRefine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { artifactId: string; instruction: string }) => {
      const { data } = await composerRefine({
        body: { artifact_id: vars.artifactId, instruction: vars.instruction },
      });
      logger.debug("[composer]", "refined", vars.instruction, "→", data?.artifact_id);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
    },
  });
}

export function useComposerDocument(assetId: string | null) {
  return useQuery({
    queryKey: ["composer", "documents", assetId],
    queryFn: async () => {
      if (!assetId) {
        return null;
      }
      const { data } = await composerGetDocument({ path: { asset_id: assetId } });
      logger.debug("[composer]", "document", assetId, data?.page_count, "pages");
      return data;
    },
    enabled: !!assetId,
    // Page thumbnails are rendered once per file and never change afterwards.
    staleTime: Infinity,
  });
}

export function useComposerLocalizePdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      assetId: string;
      pages: number[];
      requestText: string;
      spec: RequestSpec;
    }) => {
      const { data } = await composerLocalizePdf({
        path: { asset_id: vars.assetId },
        body: { pages: vars.pages, request_text: vars.requestText, spec: vars.spec },
      });
      logger.debug("[composer]", "localized", vars.pages.length, "pages →", data?.artifact_id);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPOSER_ESCALATIONS_KEY });
    },
  });
}

export function useComposerRules() {
  return useQuery({
    queryKey: ["composer", "rules"],
    queryFn: async () => {
      const { data } = await composerListRules();
      return data ?? [];
    },
  });
}

export function useComposerEscalations() {
  return useQuery({
    queryKey: COMPOSER_ESCALATIONS_KEY,
    queryFn: async () => {
      const { data } = await composerListEscalations();
      return data ?? [];
    },
  });
}

export function useComposerTranslationStatus() {
  return useQuery({
    queryKey: ["composer", "translation-status"],
    queryFn: async () => {
      const { data } = await composerTranslationStatus();
      logger.debug("[composer]", "translation provider", data?.provider, data?.available);
      return data;
    },
  });
}
